import { ActionsObservable, combineEpics, StateObservable } from 'redux-observable'
import { concat, from, Observable } from 'rxjs'
import { createUserActions, CreateUserActions } from './create-user.actions'
import { CreateUserState } from './create-user.reducer'
import { Deps } from 'src/app.store'
import { DSService } from 'src/services/ds.service'
import { filter, mergeMap } from 'rxjs/operators'
import { notification } from 'antd'
import { startFetch, stopFetch } from 'src/framework/fetch-reducer'
import { pipe } from 'fp-ts/lib/pipeable'
import * as O from 'fp-ts/lib/Option'

type Actions = CreateUserActions

interface Action$ extends ActionsObservable<Actions> {}
interface State$ extends StateObservable<CreateUserState> {}

const loadRights = (dsService: DSService) => (p: { correlationId: string }) => {
  const { correlationId } = p
  return concat(
    from(
      dsService.getRights().map(rightsOpt =>
        pipe(
          rightsOpt,
          O.fold(
            () => createUserActions.LoadRightsFailed({ correlationId }),
            rights => createUserActions.LoadRightsSuccess({ rights })
          )
        )
      )
    )
  )
}

const loadCompanies = (dsService: DSService) => (p: { correlationId: string }) => {
  const { correlationId } = p
  return concat(
    from(
      dsService.getCompanies().map(companiesOpt =>
        pipe(
          companiesOpt,
          O.fold(
            () => createUserActions.LoadCompaniesFailed({ correlationId }),
            companies => createUserActions.LoadCompaniesSuccess({ companies })
          )
        )
      )
    )
  )
}

const inviteCall = (dsService: DSService) => (p: {
  email: string
  firstName: string
  lastName: string
  rightIds: number[]
  companyId: string
  creatorToken: string
  [startFetch]: string
}): Observable<CreateUserActions> =>
  concat(
    from(
      dsService
        .inviteUser({
          email: p.email,
          firstName: p.firstName,
          lastName: p.lastName,
          rightIds: p.rightIds,
          companyId: p.companyId,
          creatorToken: p.creatorToken
        })
        .map(createdOpt =>
          pipe(
            createdOpt,
            O.fold(
              () => {
                notification.error({
                  description: "L'utilisateur n'a pas pu être créé",
                  message: 'Erreur'
                })

                return createUserActions.CreateUserFailure({
                  [stopFetch]: p[startFetch]
                })
              },
              _created => {
                notification.success({
                  description: "L'utilisateur a bien été créé et va recevoir un courriel pour choisir un mot de passe",
                  message: 'Utilisateur créé'
                })

                return createUserActions.CreateUserSuccess({
                  [stopFetch]: p[startFetch]
                })
              }
            )
          )
        )
    )
  )

export const creationEpic = (action$: Action$, _s: State$, deps: Deps) =>
  action$.pipe(
    filter(createUserActions.is.CreateUser),
    mergeMap(inviteCall(deps.dsService))
  )

export const loadRightsEpic = (action$: Action$, _s: State$, deps: Deps) =>
  action$.pipe(
    filter(createUserActions.is.LoadRights),
    mergeMap(loadRights(deps.dsService))
  )

export const loadCompaniesEpic = (action$: Action$, _s: State$, deps: Deps) =>
  action$.pipe(
    filter(createUserActions.is.LoadCompanies),
    mergeMap(loadCompanies(deps.dsService))
  )

export const createUserEpics = combineEpics(creationEpic, loadRightsEpic, loadCompaniesEpic)



// WEBPACK FOOTER //
// ./src/users-components/create-user/create-user.epics.ts